// AI Generated Code
import { Component, Input, OnInit } from '@angular/core';
import { SessionService } from '@monsido/core/session/session.service';
import { ComplianceAccessibilityService } from '@client/app/blocks/service/compliance/compliance-accessibility/compliance-accessibility.service';

@Component({
    selector: 'accessibility-dashboard-notifications',
    standalone: false,
    template: `
        <div class="accessibility-dashboard-notifications">
            <ul class="list-group d-flex align-items-stretch flex-wrap">
                <li class="list-group-item inline-block-important py-0 pr-1 pl-2">
                    <p class="pl-2 my-0 text-grey">
                        <span translate>Failing accessibility checks</span>
                        <span class="fas fa-question-circle"
                              [triggers]="'focus hover'"
                              [placement]="'right'"
                              [container]="'body'"
                              [ngbTooltip]="'Failing accessibility checks gives you information about the current Accessibility status of the website/group. It may vary from the history if there have been changes in ignored and review of accessibility issues' | translate">
                        </span>
                    </p>
                    <span class="pl-2 font-size-1-5 font-weight-400 notification-count">
                        {{checksWithIssuesResultType | monNumber}}&nbsp;<small>/{{checksCountResultType | monNumber}}</small>
                    </span>
                    <br/>

                    <p class="pl-2 d-inline-block"
                            (mouseenter)="showChangeCount = true"
                            (mouseleave)="showChangeCount = false"
                          [class.text-grey]="failingChecksDifference === 0"
                          [class.text-danger]="failingChecksDifference > 0"
                          [class.text-success]="failingChecksDifference < 0">
                        <span class="fas font-size-0-7"
                              [hidden]="failingChecksDifference === 0"
                              [class.fa-long-arrow-down]="failingChecksDifference < 0"
                              [class.fa-long-arrow-up]="failingChecksDifference > 0">
                        </span>
                        {{failingChecksPercentage/100 | monNumber:'0.[00]%'}}
                    </p>
                    <p class="text-grey inline-block mb-0" [hidden]="!showChangeCount">
                        <span *ngIf="failingChecksDifference > 1" translate>
                            Failing checks found
                        </span>
                        <span *ngIf="failingChecksDifference === 1" translate>
                            Failing check found
                        </span>
                        <span *ngIf="failingChecksDifference == 0" translate>
                            No changes
                        </span>
                        <span *ngIf="failingChecksDifference === -1" translate>
                            Failing check
                        </span>
                        <span *ngIf="failingChecksDifference < -1" translate>
                            Failing checks
                        </span>
                    </p>
                </li>
                <li class="list-group-item inline-block-important py-0 pr-1 pl-2">
                    <p class="pl-2 my-0 text-grey" translate>
                        Pages with failing checks
                    </p>
                    <span class="pl-2 font-size-1-5 font-weight-400 notification-count">
                        {{pagesWithIssuesResultType | monNumber}}
                    </span>
                </li>
            </ul>
        </div>
    `,
})
export class AccessibilityDashboardNotificationsComponent implements OnInit {
    @Input() summary: any;
    @Input() selectedObs: any;
    @Input() history: any;
    @Input() accessibilityChecks: any;

    resultType: any;
    pagesWithIssuesResultType: any;
    checksWithIssuesResultType: any;
    checksCountResultType: any;
    failingChecksDifference: any;
    failingChecksPercentage: any;
    showChangeCount: any;
    tooltipText = 'Failing accessibility checks gives you information about the current Accessibility status of the website/group.' +
        ' It may vary from the history if there have been changes in ignored and review of accessibility issues';

    constructor (
        private sessionService: SessionService,
        private complianceAccessibilityService: ComplianceAccessibilityService,
    ) {}

    ngOnInit (): void {
        this.selectedObs.subscribe((resultType: any) => {
            this.resultType = resultType;
            this.pagesWithIssuesResultType = this.getPagesWithIssuesByType();
            this.checksWithIssuesResultType = this.getChecksWithIssuesByType();
            this.checksCountResultType = this.getChecksCountBySelectedResultType();
            this.failingChecksDifference = this.getFailingChecksDifference();
            this.failingChecksPercentage = this.getFailingChecksPercentage();
        });
    }

    getPagesWithIssuesByType (): any {
        if (!this.summary) {
            return 0;
        }
        switch (this.resultType) {
            case 'errors':
                return this.summary.pages_by_result_type.error || 0;
            case 'errors_and_warnings':
                return this.summary.pages_by_result_type.error_and_warning || 0;
            default:
                return this.summary.pages_with_issues || 0;
        }
    }

    getChecksWithIssuesByType (): any {
        switch (this.resultType) {
            case 'errors':
                return this.getChecksCountByResultType(['error'], true);
            case 'errors_and_warnings':
                return this.getChecksCountByResultType(['error', 'warning'], true);
            default:
                return this.getChecksCountByResultType([], true);
        }
    }

    getChecksCountBySelectedResultType (): any {
        switch (this.resultType) {
            case 'errors':
                return this.getChecksCountByResultType(['error']);
            case 'errors_and_warnings':
                return this.getChecksCountByResultType(['error', 'warning']);
            default:
                return this.getChecksCountByResultType();
        }
    }

    getFailingChecksDifference (): any {
        if (this.history.length < 1) {
            return 0;
        }

        return this.getChecksWithIssuesByType() - this.getChecksWithIssuesByTypeInHistory(this.history[this.history.length - 1]);
    }

    getFailingChecksPercentage (): any {
        if (this.history.length < 1) {
            return 0;
        }
        const current = this.getChecksWithIssuesByType();
        const last = this.getChecksWithIssuesByTypeInHistory(this.history[this.history.length - 1]);
        const min = Math.min(last, current);
        const max = Math.max(last, current);
        if (min === 0 && max > 0) {
            return 100;
        } else if (min === max) {
            return 0;
        } else {
            return (min / max) * 100;
        }
    }

    getChecksWithIssuesByTypeInHistory (crawl: any): any {
        let result = 0;
        const accessibilityParameters = this.getAccessibilityChecksParameters();

        for (let i = 0; i < accessibilityParameters.length; i++) {
            result += crawl.accessibility_checks[accessibilityParameters[i]] || 0;
        }

        return result;
    }

    getAccessibilityChecksParameters (): any {
        let type;
        if (['errors', 'errors_and_warnings'].indexOf(this.resultType) > -1) {
            type = this.resultType;
        } else {
            type = this.sessionService.domain?.features?.accessibility;
        }

        switch (type) {
            case 'errors':
                return ['error'];
            case 'errors_and_warnings':
                return ['error', 'warning'];
            case 'WCAG2-A':
                return ['A'];
            case 'WCAG2-AA':
                return ['A', 'AA'];
            case 'WCAG2-AAA':
                return ['A', 'AA', 'AAA'];
            case 'WCAG21-A':
                return ['A'];
            case 'WCAG21-AA':
                return ['A', 'AA'];
            case 'WCAG21-AAA':
                return ['A', 'AA', 'AAA'];
            default:
                return [];
        }
    }

    getChecksCountByResultType (resultTypes?: any, onlyErrors?: any): any {
        const params = {
            result_types: resultTypes,
            only_errors: onlyErrors,
        };
        const checks = this.complianceAccessibilityService.filterAccessibilityChecksByResultTypes(this.accessibilityChecks, params);
        return checks.length;
    }
}
